<template>
  <div
    ref="mapObserver"
    class="m-0"
  >
    <!-- Top control buttons -->
    <div class="toolbar">
      <!-- New geometry additions -->
      <div
        class="toolbar-item dropdown"
        @mouseenter="(event) => openDropdown('newLayer', event)"
        @mouseleave="closeDropdown"
      >
        <button class="toolbar-btn">
          {{ $t('orders.add_layer') }} ▼
        </button>
        <div
          v-if="activeDropdown === 'newLayer'"
          class="dropdown-menu"
        >
          <button
            :disabled="loading || importFromFile || preEditedGeometriesState !== null"
            class="dropdown-item"
            @click="importNewGeometry"
          >
            {{ $t('common.import_from_file') }}
          </button>
          <button
            :disabled="preEditedGeometriesState !== null"
            class="dropdown-item"
            @click="showGeometryModal = true"
          >
            {{ $t('orders.add_empty_geometry') }}
          </button>
        </div>
      </div>
      <!-- Map data switches -->
      <div
        class="toolbar-item dropdown"
        @mouseenter="(event) => openDropdown('mapdata', event)"
        @mouseleave="closeDropdown"
      >
        <button class="toolbar-btn">
          {{ $t('orders.map_data') }} ▼
        </button>
        <div
          v-if="activeDropdown === 'mapdata'"
          class="dropdown-menu"
        >
          <button
            class="dropdown-item"
            @click="toggleObservations"
          >
            {{ observationsEnabled ? $t('orders.hide_observations') : $t('orders.show_observations') }}
          </button>
          <button
            class="dropdown-item"
            @click="toggleStorages"
          >
            {{ !storagesEnabled ? $t('orders.show_storages') : $t('orders.hide_storages') }}
          </button>
          <button
            class="dropdown-item"
            @click="toggleContractAreas"
          >
            {{ !areasEnabled ? $t('orders.show_areas') : $t('orders.hide_areas') }}
          </button>
        </div>
      </div>
      <!-- Copy & Paste -->
      <div
        class="toolbar-item dropdown"
        @mouseenter="(event) => openDropdown('copyPaste', event)"
        @mouseleave="closeDropdown"
      >
        <button class="toolbar-btn">
          {{ $t('common.copy') }} ▼
        </button>
        <div
          v-if="activeDropdown === 'copyPaste'"
          class="dropdown-menu"
        >
          <button
            class="dropdown-item"
            :disabled="!hasLayers"
            @click="copyAllGeometries"
          >
            {{ $t('orders.copy_all_geometry') }}
          </button>
          <button
            class="dropdown-item"
            :disabled="!hasLayers || !polygonDrawing"
            @click="copyInsidePolygon"
          >
            {{ $t('orders.copy_geometry_inside_polygon') }}
          </button>
          <button
            class="dropdown-item"
            :disabled="!hasLayers || !selectedLayers || selectedLayers.length < 1"
            @click="copySelectedGeometry"
          >
            {{ $t('orders.copy_selected_geometry') }}
          </button>
        </div>
      </div>
      <div class="toolbar-item">
        <button
          class="toolbar-btn"
          :disabled="$store.state.copiedGeometries === null"
          @click="pasteGeometry"
        >
          {{ $t('common.paste') }}
        </button>
      </div>
      <!-- Export -->
      <div
        class="toolbar-item dropdown"
        @mouseenter="(event) => openDropdown('export', event)"
        @mouseleave="closeDropdown"
      >
        <button class="toolbar-btn">
          {{ $t('common.export') }} ▼
        </button>
        <div
          v-if="activeDropdown === 'export'"
          class="dropdown-menu"
        >
          <button
            class="dropdown-item"
            :disabled="!hasLayers"
            @click="exportAllGeometry"
          >
            {{ $t('common.all') }}
          </button>
          <button
            class="dropdown-item"
            :disabled="!hasLayers || !polygonDrawing"
            @click="exportGeometryInsidePolygon"
          >
            {{ $t('orders.export_inside_polygon') }}
          </button>
          <button
            class="dropdown-item"
            :disabled="!hasLayers || !selectedLayers || selectedLayers.length < 1"
            @click="exportSelectedGeometry"
          >
            {{ $t('orders.export_selected_geometry') }}
          </button>
        </div>
      </div>
      <!-- Remove -->
      <div
        class="toolbar-item dropdown"
        @mouseenter="(event) => openDropdown('edit', event)"
        @mouseleave="closeDropdown"
      >
        <button class="toolbar-btn">
          {{ $t('common.remove') }} ▼
        </button>
        <div
          v-if="activeDropdown === 'edit'"
          class="dropdown-menu"
        >
          <button
            class="dropdown-item"
            :disabled="!hasLayers || !polygonDrawing "
            @click="deleteGeometriesByPolygon"
          >
            {{ $t('orders.export_inside_polygon') }}
          </button>
          <button
            class="dropdown-item"
            :disabled="!hasLayers || !selectedLayers || selectedLayers.length < 1"
            @click="removeGeometryPaths"
          >
            {{ $t('orders.export_selected_geometry') }}
          </button>
        </div>
      </div>

      <div class="toolbar-item">
        <button
          class="toolbar-btn"
          :disabled="loading || removedGeometries.length === 0"
          @click="restoreGeometryPath"
        >
          {{ $t('orders.restore_previous_geometry') }}
        </button>
      </div>

      <!-- Line drawing -->
      <div class="toolbar-item">
        <button
            class="toolbar-btn"
            :disabled="!addPolylineEnabled && !addPointEnabled"
            @click="handleSaveClick"
        >
            {{ $t('common.save') }}
        </button>
      </div>
        <div class="toolbar-item">
        <button
          class="toolbar-btn"
          :disabled="selectedLayers.length !== 1 || activeFeature !== ''"
          @click="editGeometry(getFeatureGeometryType(selectedLayers[0].layer))"
        >
          {{ $t('common.edit') }}
        </button>
      </div>
      <div class="toolbar-item">
        <button
          class="toolbar-btn"
          :disabled="(!addRoadByClick && (!addPolylineEnabled && !addPointEnabled) && geometryUpdates === 0) || (addRoadByClick && geometryUpdates === 0)"
          @click="handleUndoLastEdit"
        >
          {{ $t('common.undo') }}
        </button>
      </div>
      <div class="toolbar-item">
        <button
          class="toolbar-btn"
          :disabled="(!addPolylineEnabled && !addPointEnabled) || (activeFeature !== 'polyline' && activeFeature !== 'point')"
          @click="resetDrawing"
        >
          {{ $t('common.cancel') }}
        </button>
      </div>

      <!-- Road clicking buttons -->
      <div class="toolbar-item">
        <button
          class="toolbar-btn"
          :disabled="!addRoadByClick && !addRoadBySearch && !addPointEnabled && !addPolylineEnabled"
          @click="setRoadAddDisabled"
        >
          {{ $t('orders.selection_ready') }}
        </button>
      </div>
    </div>

    <!-- Map and controls -->
    <div style="position: relative; height: 55vh; padding: 1em; text-align: left;">
      <map-container
        ref="mapContainer"
        :menu-items="menuItems"
        :find-user="false"
        @onMapChanged="mapChanged"
        @onMapClicked="mapClicked"
        @onGeoJsonClicked="onGeoJsonClicked"
        @onGeoJsonHoverEvent="onGeoJsonHoverEvent"
        @onGeoJsonHoverExitEvent="onGeoJsonHoverExitEvent"
        @onZoomLevelChange="onZoomLevelChange"
        @onMarkerTap="onMarkerTap"
        @pointerCoordinateMove="pointerCoordinateMove"
        @onDrag="onDrag"
        @onDragEnd="onDragEnd"
      />

      <div class="map-controls">
        <div class="map-controls__list col-12 col-lg-4">
          <measure-road-map
            v-if="measureRoadEnabled"
            ref="measureRoad"
            :map="map"
            style="float: right; z-index: 9999; position: relative"
            :draggable="true"
            :confirm-text="$t('common.save')"
            @close="closeRoadMeasurement"
            @confirm-selection="setNewGeometryFromMeasure"
          />
          <measure-distance-map
            v-if="measureDistanceEnabled"
            ref="measureDistances"
            :map="map"
            style="float: right; z-index: 2"
            :measure-distance-results="measureDistances"
            :measure-distance-target-item="measureDistanceTargetItem"
            :measure-distance-target-item-callback="measureDistanceTargetItemCallback"
            :draggable="true"
            @on-remove-measure-distances="removeMeasureDistances"
          />
          <selection-tool
            v-if="polygonDrawing"
            :polygon-drawing="polygonDrawing"
            :show-edit-selection="false"
            :showing-observations="observationsEnabled"
            :enable-observation-to-geometry="observationsEnabled"
            @observations-to-road-geometry="showObservationsToRoadGeometry"
            @show-selection-selector="showSelectionSelector"
            @clear-polygon="clearPolygon"
            @close="hidePolygonDrawing"
          />
          <observation-map
            v-if="observationsEnabled"
            ref="observations"
            :map="map"
            :user="user"
            :is-observer="isObserver"
            :selected-contract-ids="[contractId]"
            :selected-order="orderId"
          />
          <material-storage-map
            v-if="storagesEnabled"
            ref="storages"
            :map="map"
            :selected-contract-id="contractId"
            :require-bounding-box="false"
            :zoom-level-in-range="true"
          />
          <map-areas
            v-if="areasEnabled"
            ref="mapAreas"
            :map="map"
            :draw-all="true"
            :company="user.company.id"
            :selected-contract-ids="[contractId]"
            :show-labels="areaLabels"
            @updateAreaLabelsVisibility="toggleAreaLabels"
          />
        </div>
      </div>
      <road-search-wizard
        v-if="showRoadSearchWizard"
        :map="map"
        @close="showRoadSearchWizard = false; loadedGeometrySelected = null;"
        @road-selected="addGeoJsonFromSearchToMap"
      />
      <address-search-wizard
        v-if="showAddressSearchWizard"
        :map="map"
        :get-coordinate="true"
        @point-fetched="addPointFromSearchToMap"
        @close="showAddressSearchWizard = false; loadedGeometrySelected = null;"
      />
      <material-storage-editor-modal
        v-model="editStorage"
        :user="user"
        @cancel="editStorage = null"
        @close-and-update="closeAndUpdateStorage"
      />
      <import-file-wizard
        v-if="importFromFile"
        ref="importfilewizardref"
        :user="user"
        @close="importFromFile = false"
        @add-layer="addLayerFromGeoJson"
        @shape-selected="shapeFileSelected"
      />

      <base-modal
        v-if="showGeometryModal"
        hide-footer
        hide-header
        @close="showGeometryModal = false"
      >
        <order-geometry-layer-editor
          :is-editing="isEditingGeometry"
          :excluded-fields="excludedFields"
          :geometry-data="currentGeometry"
          @close="closeGeometryModal"
          @submit="saveGeometry"
        />
      </base-modal>

      <base-modal
        v-if="geometriesForDownload"
        size="lg"
        hide-footer
        hide-header
        @close="geometriesForDownload = null"
      >
        <geometry-layer-download
          :geometry-layer="geometriesForDownload"
          :excluded-fields="excludedFields"
          :coordinate-systems="coordinateSystems"
          :download-modal="showDownloadAsFileModal"
          @download-geo-json="downloadGeoJson"
          @download-shape="downloadShape"
          @close="closeDownloadGeometryModal"
        />
      </base-modal>

      <order-geometry-add-property-modal
        :show-add-properties-modal="showAddPropertiesModal"
        :loaded-geometry-selected="loadedGeometrySelected"
        :loaded-geometries="loadedGeometries"
        :selected-layer="selectedLayers[0]"
        :add-point-enabled="addPointEnabled"
        :add-polyline-enabled="addPolylineEnabled"
        :new-properties="newProperties"
        :excluded-fields="excludedFields"
        @add-property="addNewPropertyToExisting"
        @on-add-to-geometry="addToGeometry"
        @remove-property="removeExistingProperty"
        @on-reset-drawing="resetDrawing(true)"
      />
    </div>
    <!-- Layer rows -->
      <div class="d-flex flex-row p-2">
          <div class="form-check form-switch">
              <input class="form-check-input" type="checkbox" role="switch" id="flexSwitchCheckDefault" v-model="showDrivingTime">
              <label class="form-check-label" for="flexSwitchCheckDefault">{{ $t('orders.extended_information') }}</label>
          </div>
      </div>


      <order-geometry-summary
        v-if="showDrivingTime && loadedGeometries && loadedGeometries.length > 0"
        :total-geometry-distance="totalGeometryDistance"
        :total-road-distance="totalRoadDistance"
      />
      <div
      v-if="loadedGeometries && loadedGeometries.length > 0"
      class="table-responsive"
    >
      <table
        class="table align-middle table-borderless"
      >
        <thead>
          <tr>
            <th scope="col" style="min-width: 170px">
              {{ $t('orders.geometry_layer') }}
            </th>
            <th scope="col">
              {{ $t('orders.geometry_color') }}
            </th>
            <th scope="col">
              {{ $t('orders.geometry_highlight') }}
            </th>
            <th scope="col">
              {{ $t('orders.geometry_label') }}
            </th>
            <th scope="col">
              {{ $t('orders.geometry_calculate') }}
            </th>
            <th scope="col">
              {{ $t('orders.geometry_polyline_indicators') }}
            </th>
            <th scope="col">
              {{ $t('orders.geometry_visibility') }}
            </th>
            <th scope="col">
              {{ $t('orders.geometry_km_distance') }}
            </th>
            <th scope="col">
              {{ $t('orders.total_point_count') }}
            </th>
            <th scope="col">
              {{ $t('orders.line_width_text') }}
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          <template
            v-for="(item, index) in sortGeometryAscending"
            :key="index"
          >
            <tr
              :class="{ 'table-row-highlighted': item.highlighted }"
              style="cursor: pointer;"
            >
              <td>
                <input
                  v-model="item.name"
                  class="editable-input"
                  :title="item.name"
                  :disabled="isSubContractor"
                  type="text"
                  @click.stop="zoomToGeometry(item)"
                >
              </td>
              <td>
                <color-picker
                  :id="'layer-color' + item.name + index"
                  :no-red-color="true"
                  :disabled="isSubContractor"
                  :default-color="item.color"
                  @select-color="(color) => selectColor(color, index)"
                />
              </td>
              <td>
                <input
                  :id="'highlight-' + index"
                  :disabled="isSubContractor"
                  class="form-check-input"
                  :checked="item.highlighted"
                  @click.stop="toggleGeometryHighlight(index)"
                  type="checkbox"
                >
              </td>
              <td>
                <select
                  v-if="item.geometry && item.geometry.features && item.geometry.features[0] && item.geometry.features[0].properties"
                  :id="'layer-label' + item.name + index"
                  v-model="item.label"
                  :disabled="isSubContractor"
                  class="form-select form-select-sm col-sm-3"
                  @change="updateLabel(index)"
                >
                  <option :value="null">
                    {{ $t('orders.select_geometry_label') }}
                  </option>
                  <option
                    v-for="propKey in getAllProperties(item)"
                    :key="propKey"
                    :value="propKey"
                  >
                    {{ propKey }}
                  </option>
                </select>
              </td>
              <td>
                <input
                  :id="'calculate-' + index"
                  v-model="item.calculate"
                  :disabled="isSubContractor"
                  class="form-check-input"
                  type="checkbox"
                >
              </td>
              <td>
                <input
                  :id="'addPolylineEnds-' + index"
                  v-model="item.addPolylineEnds"
                  :disabled="isSubContractor"
                  class="form-check-input"
                  type="checkbox"
                  @change="updateGeoJsonMapObject(index, item)"
                >
              </td>
              <td>
                <input
                  :id="'visibility-' + index"
                  class="form-check-input"
                  :disabled="isSubContractor"
                  type="checkbox"
                  :checked="item.visibility"
                  @change="handleVisibilityChange(index)"
                >
              </td>
              <td>
                {{ getLengthInKilometers(item.geometry) }}
              </td>
              <td>
                {{ getLoadedGeometryType(item) === 'point' ? getPointCount(item.geometry) : '-' }}
              </td>
              <td style="width: 5em;">
                <select
                  id="lineWidth"
                  v-model="item.lineWidth"
                  class="form-select form-select-sm"
                  :disabled="isSubContractor"
                  @change="updateGeoJsonMapObject(index, item)"
                >
                  <option :value="null">
                    {{ $t('orders.select_line_width') }}
                  </option>
                  <option
                    v-for="option in lineWidthOptions"
                    :key="option"
                    :value="option"
                  >
                    {{ option }}
                  </option>
                </select>
              </td>

              <td style="overflow-y: visible !important">
                <div class="d-flex flex-row">
                  <!-- Button Group for Move Up/Down -->
                  <div class="btn-group-vertical">
                    <button
                      type="button"
                      class="btn btn-dark custom-btn"
                      :disabled="index === 0 || isSubContractor"
                      @click.stop="move(index, up)"
                    >
                      <i class="fa fa-chevron-up" />
                    </button>
                    <button
                      type="button"
                      class="btn btn-dark custom-btn"
                      :disabled="index === sortGeometryAscending.length - 1 || isSubContractor"
                      @click.stop="move(index, down)"
                    >
                      <i class="fa fa-chevron-down" />
                    </button>
                  </div>

                  <!-- Button Group for Trash + Dropdown -->
                  <button
                    v-if="!isSubContractor"
                    :id="'removeGeometry' + item.name + index"
                    type="button"
                    class="btn btn-outline-danger btn-sm mx-2"
                    :disabled="clickDisabledByGeometry(index)"
                    :title="$t('common.remove')"
                    @click.stop="openRemoveGeometryModal(item, index)"
                  >
                    <i
                      class="fa fa-trash"
                      style="font-size: .8em; padding: 0; line-height: 1em;"
                    />
                  </button>

                  <!-- Dropdown for 'polyline' -->
                  <div
                    v-if="getLoadedGeometryType(item) === 'polyline' && !isSubContractor"
                    class="position-relative dropdown"
                    @mouseenter="(event) => openDropdown('layer'+index, event)"
                    @mouseleave="closeDropdown"
                  >
                    <button
                      class="btn btn-sm btn-outline-dark"
                      :disabled="clickDisabledByGeometry(index)"
                    >
                      <i
                        class="fa fa-ellipsis-h"
                        style="font-size: .8em; padding: 0; line-height: 1em;"
                      />
                    </button>
                    <div
                      v-if="activeDropdown === 'layer'+index"
                      class="dropdown-menu-left-top"
                    >
                      <button
                        class="dropdown-item"
                        @click="setNewGeometryByDrawing(index, getLoadedGeometryType(item))"
                      >
                        {{ $t('map.add_road_by_drawing') }}
                      </button>
                      <button
                        class="dropdown-item"
                        @click="openRoadSearchWizard(index)"
                      >
                        {{ $t('map.add_road_by_search') }}
                      </button>
                      <button
                        class="dropdown-item"
                        @click="setRoadAddEnabled(index)"
                      >
                        {{ $t('map.add_road') }}
                      </button>
                      <button
                        class="dropdown-item"
                        @click="openRoadMeasurement(index)"
                      >
                        {{ $t('map.add_road_by_measurement') }}
                      </button>
                      <button
                        class="dropdown-item"
                        @click="openEditGeometryModal(item)"
                      >
                        {{ $t('orders.edit_layer') }}
                      </button>
                      <button
                        class="dropdown-item"
                        @click="openDownloadGeometryModal(item)"
                      >
                        {{ $t('trip.download_as_file') }}
                      </button>
                    </div>
                  </div>

                  <!-- Dropdown for 'point' -->
                  <div
                    v-if="getLoadedGeometryType(item) === 'point' && !isSubContractor"
                    class="position-relative dropdown"
                    style="z-index: 9999"
                    @mouseenter="(event) => openDropdown('layer'+index, event)"
                    @mouseleave="closeDropdown"
                  >
                    <button
                      class="btn btn-sm btn-outline-dark"
                      :disabled="clickDisabledByGeometry(index)"
                    >
                      <i
                        class="fa fa-ellipsis-h"
                        style="font-size: .8em; padding: 0; line-height: 1em;"
                      />
                    </button>
                    <div
                      v-if="activeDropdown === 'layer'+index"
                      class="dropdown-menu-left-top"
                    >
                      <button
                        class="dropdown-item"
                        @click="setNewGeometryByDrawing(index, getLoadedGeometryType(item))"
                      >
                        {{ $t('map.add_point_by_click') }}
                      </button>
                      <button
                        class="dropdown-item"
                        @click="openAddressSearchWizard(index)"
                      >
                        {{ $t('map.add_point_by_search') }}
                      </button>
                      <button
                        class="dropdown-item"
                        @click="openEditGeometryModal(item)"
                      >
                        {{ $t('orders.edit_layer') }}
                      </button>
                      <button
                        class="dropdown-item"
                        @click="openDownloadGeometryModal(item)"
                      >
                        {{ $t('trip.download_as_file') }}
                      </button>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
    <hr class="ms-2 me-2">
    <div class="col-12 d-flex justify-content-end pb-2">
      <button
        class="btn btn-secondary form-button"
        :disabled="loading"
        @click.stop="close"
      >
        {{ $t('common.cancel') }}
      </button>
      <button
        v-if="loadedGeometries || loadedGeometries.length > 0"
        class="btn btn-success form-button"
        :disabled="loading || importFromFile || addPolylineEnabled || addPointEnabled || isSubContractor"
        @click.stop="emitGeometry"
      >
        {{ $t('common.save') }}
      </button>
    </div>

    <!-- Order copy confirmation -->
    <base-modal
      v-if="showExportGeometriesModal"
      hide-header
      hide-footer
      @close="cancelCopying"
    >
      <order-geometry-export-modal
          ref="exportModal"
          :contract-id="contractId"
          @cancel-copy="cancelCopying"
          @copy-geometry="doCopyOrderGeometry"
      />
    </base-modal>

      <!-- Modal for Deletion Confirmation -->
      <base-modal
          v-if="deleteGeometryModalOpen"
          :hide-footer="true"
          @close="deleteGeometryModalOpen=false"
      >
          <div class="modal-dialog modal-dialog-centered">
              <div class="modal-content">
                  <div class="modal-body ">
                      <h5 class="modal-title justify-content-center text-center" id="modalTitle">{{ $t('common.confirm_order_geometry_delete') }}</h5>
                  </div>
                  <div class="modal-footer justify-content-center">
                      <button type="button" class="btn btn-secondary me-2" @click="closeConfirmRemoveGeometryAlert()">{{ $t('common.no') }}</button>
                      <button type="button" class="btn btn-danger" @click="confirmRemoveGeometry()">{{ $t('common.yes') }}</button>
                  </div>
              </div>
          </div>
      </base-modal>

    <!-- Observation to order geometry -->
    <observation-to-road-geometry-modal ref="observationToRoadGeometryModal" />

    <!-- Street view -->
    <google-street-view-link
        v-if="streetViewDialogCoordinates"
        ref="streetView"
        :latLon="streetViewDialogCoordinates"
        :hide-text="true"
        @dialog-closed="streetViewDialogCoordinates = null"
    />

      <div
          v-if="loading"
          id="loader"
          class="spinner"
    />
  </div>
</template>

<script>
import {orderMixin} from "@/components/mixins/OrderMixin";
import {measureDistanceMixin} from "../mixins/MeasureDistanceMixin";
import {selectFromMapMixin} from "@/components/mixins/SelectFromMapMixin";
import {coordinateSystems} from "@/components/mixins/CoordinateConversionMixin";
import {geometryMixin} from '../mixins/GeometryMixin'
import MeasureRoadMap from "@/components/map/MeasureRoadMap";
import MeasureDistanceMap from "@/components/map/MeasureDistanceMap";
import MapContainer from "../map/MapContainer";
import {restApi} from "../mixins/RestApiMixin";
import {mapHelper} from '../mixins/MapMixin'
import {timeUtils} from '../mixins/TimeUtils'
import {vehicleHelper} from "../mixins/VehicleMixin";
import ShapeImportForm from "../shapeimport/ShapeImportForm";
import {mapState} from "vuex"
import ColorPicker from "@/components/ColorPicker"
import RoadSearchWizard from "../search/RoadSearchWizard";
import AddressSearchWizard from "../search/AddressSearchWizard";
import OrderGeometryAddPropertyModal from "./OrderGeometryAddPropertyModal";
import ObservationMap from "../observation/ObservationMap.vue";
import MaterialStorageMap from "../materialstorage/MaterialStorageMap.vue";
import MapAreas from "../area/MapAreas.vue";
import MaterialStorageEditorModal from "../materialstorage/MaterialStorageEditorModal.vue";
import ImportFileWizard from "@/components/shapeimport/ImportFileWizard.vue";
import SelectionTool from "@/components/map/SelectionTool.vue";
import {spatialMixin} from "@/components/mixins/SpatialMixin";
import ObservationToRoadGeometryModal from "../map/ObservationToRoadGeometryModal.vue";
import OrderGeometryLayerEditor from "@/components/order/OrderGeometryLayerEditor.vue";
import GeometryLayerDownload from "@/components/order/GeometryLayerDownload.vue";
import {downloadHelper} from "@/components/mixins/DownloadMixin";
import OrderGeometryExportModal from "@/components/order/OrderGeometryExportModal.vue";
import BaseModal from "@/components/BaseModal";
import {toast} from "vue3-toastify";
import {inject} from "vue";
import OrderGeometrySummary from "@/components/order/OrderGeometrySummary.vue";
import GoogleStreetViewLink from "@/components/utils/GoogleStreetViewLink";

export default {
    name: "OrderGeometry",
    components: {
        GoogleStreetViewLink,
        OrderGeometrySummary,
        BaseModal,
        OrderGeometryExportModal,
        ObservationToRoadGeometryModal,
        OrderGeometryLayerEditor,
        GeometryLayerDownload,
        SelectionTool,
        ImportFileWizard,
        MaterialStorageEditorModal,
        MapAreas,
        ObservationMap, MaterialStorageMap, MeasureDistanceMap, OrderGeometryAddPropertyModal,
        ShapeImportForm, MapContainer,
        ColorPicker, MeasureRoadMap, RoadSearchWizard, AddressSearchWizard},
    mixins: [orderMixin, restApi, timeUtils, vehicleHelper, measureDistanceMixin, selectFromMapMixin,
        geometryMixin, mapHelper, spatialMixin, downloadHelper],
    props: {
        geometries: {
            type: Array,
            default() {
                return []
            }
        },
        contractId: {
            type: Number,
            default: null
        },
        contract: {
            type: Object,
            default: null
        },
        orderId: {
            type: Number,
            default: null
        },
        user: {
            type: Object,
            default: null
        },
        isObserver: {
            type: Boolean,
            default: false
        },
        isSubContractor: {
            type: Boolean,
            default: false
        },
        addGeometriesToMapOnLoad: {
            type: Boolean,
            default: true
        },
        allowLayersPostProcessing: {
            type: Boolean,
            default: false
        },
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            showDrivingTime: false,
            activeDropdown: null,
            streetViewDialogCoordinates: null,
            importFromFile: false,
            newName: null,
            newColor: null,
            labelShown: false,
            calcDistance: null,
            showTimeCalculator: false,
            exportGeometries: [],
            geometriesForDownload: null,
            showExportGeometriesModal: false,
            contractSet: null,
            lineWidthOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
            newProperties: null,
            map: null,
            loading: false,
            drawnFigure: false,
            shpFile: null,
            shxFile: null,
            dbfFile: null,
            showRoadSearchWizard: false,
            showAddressSearchWizard: false,
            showAddPropertiesModal: false,
            measureDistances: [],
            measureDistanceEnabled: false,
            measureDistanceTargetItem: null,
            addPolylineEnabled: false,
            addPointEnabled: false,
            polygonDrawing: false,
            addRoadByClick: false,
            addRoadBySearch: false,
            preEditedGeometriesState: null,
            geometryUpdates: 0,
            newGeometryType: "Point",
            newPolyLineId: -1,
            newPolygonId: -1,
            selectedGeometryItems: [],
            activeFeature: "",
            newFeature: "",
            removeFeature: true,
            editStorage: null,
            showGeometryModal: false,
            isEditingGeometry: false,
            currentGeometry: {},
            showConfirmRemoveFeature: false,
            showDownloadAsFileModal: false,
            geoJsonToAdd: null,
            loadedGeometrySelected: null,
            storagesEnabled: false,
            areasEnabled: false,
            measureRoadEnabled: false,
            observationsEnabled: false,
            loadedGeometries: [],
            geometryModified: false,
            selectedLayers: [],
            removedGeometries: [],
            addLineEnds: false,
            up: -1,
            down: 1,
            observer: null,
            deleteGeometryModalOpen: false,
            removableItem: null,
            removableIndex: null,
            areaLabels: false,
        }
    },

    watch: {
        addRoadByClick() {
            if(this.addRoadByClick) {
              this.addGeometryByClickToast()
            }
        },
        addPolylineEnabled() {
            if(this.addPolylineEnabled) {
                this.addGeometryToast()
            }
        },
    },
    created() {
        this.loadedGeometries = [];
    },
    beforeUnmount() {
        if (this.observer) {
            this.observer.disconnect();
        }
    },
    computed: {
        ...mapState({
            selectedMap: state => state.mapSettings.baseMap,
        }),

        hasLayers() {
            return this.loadedGeometries.length > 0
        },

        sortGeometryAscending() {
            const geometries = this.loadedGeometries;
            return geometries.sort((a, b) => a.showOrder - b.showOrder)
        },

        coordinateSystems() {
            return coordinateSystems
        },

        menuItems() {
            return [
                {
                    text: "Google Street View",
                    onClick: this.showStreetView
                },
                !this.measureDistanceEnabled ? {
                    text: this.$t('map.measure_distance'),
                    onClick: this.initMeasureDistance
                } : {
                    text: this.$t('map.remove_measurements'),
                    onClick: this.removeMeasureDistances
                },
                !this.polygonDrawing ? {
                    text: this.$t('map.select_assets'),
                    onClick: this.enablePolygonDrawing
                } : {
                    text: this.$t('map.end_selection'),
                    onClick: this.showSelectionSelector
                }
            ]
        },
        hasPolylines() {
            const polylines = this.getPolyline(this.OTHER);
            return polylines && polylines.length > 0;
        },
        addByDrawingEnabled() {
            return this.addPolylineEnabled || this.addPointEnabled
        },

        totalGeometryDistance() {
            return this.loadedGeometries.reduce((total, geometry) => {
                const distance = this.getGeometryLength(geometry.geometry, true) || 0;
                return total + distance;
            }, 0);
        },

        totalRoadDistance() {
            return this.loadedGeometries.reduce((total, geometry) => {
                const distance = this.getLengthInKilometers(geometry.geometry, true) || 0;
                return total + distance;
            }, 0);
        },

    },
    methods: {
        toggleAreaLabels(toggle) {
            this.areaLabels = toggle
        },

        getPointCount(geometry) {
            if (!geometry || !geometry.features) return 0;
            return geometry.features.filter(feature =>
                feature.geometry && feature.geometry.type === 'Point'
            ).length;
        },
        toggleGeometryHighlight(index) {
            const item = this.loadedGeometries[index];
            if (!item) return;

            // Simply toggle the highlight state without affecting other layers
            item.highlighted = !item.highlighted;

            // Refresh map to show highlighting
            this.refreshMapHighlighting();
        },

        refreshMapHighlighting() {
            if (!this.map) return;

            // Remove all layers and re-add with highlighting
            this.hideImportedObjects();
            this.addGeoJsonMapObjects(false, false);
        },
        // Toolbar methods
        openDropdown(name, event) {
            this.activeDropdown = name;


            this.$nextTick(() => {
                const dropdown = document.querySelector('.dropdown-menu-left-top');
                const button = event.target.closest('.dropdown');

                if (dropdown && button) {
                    const buttonRect = button.getBoundingClientRect();
                    const dropdownHeight = dropdown.offsetHeight;
                    const dropdownWidth = dropdown.offsetWidth;
                    const viewportHeight = window.innerHeight;

                    let top = buttonRect.bottom - dropdownHeight;
                    let left = buttonRect.left - dropdownWidth;

                    if (top < 0) {
                        top = buttonRect.bottom;
                    }

                    if (top + dropdownHeight > viewportHeight) {
                        top = viewportHeight - dropdownHeight - 10;
                    }

                    if (left < 0) {
                        left = buttonRect.right;
                    }

                    dropdown.style.top = Math.max(10, top) + 'px';
                    dropdown.style.left = Math.max(10, left) + 'px';
                }
            });
        },
        closeDropdown() {
            this.activeDropdown = null;
        },

        move(index, direction) {
            const currentGeom = this.sortGeometryAscending[index];
            const prevGeom = this.sortGeometryAscending[index + direction];
            let currentIndex = this.loadedGeometries.findIndex(v => v.importedItemIndex === currentGeom.importedItemIndex);
            let geoIndex = this.loadedGeometries.findIndex(v => v.importedItemIndex === prevGeom.importedItemIndex);
            const temp = this.loadedGeometries[currentIndex].showOrder
            this.loadedGeometries[currentIndex].showOrder = this.loadedGeometries[geoIndex].showOrder
            this.loadedGeometries[geoIndex].showOrder = temp
            this.loading = true
            setTimeout(() => {
                this.hideImportedObjects()
                this.addGeoJsonMapObjects(false, false)
                this.loading = false
            }, 20)
        },

        close: function () {
            this.selectedLayers = []
            this.$emit('close')
        },

        clickDisabledByGeometry(loadedGeometryIndex) {
            // you can only do changes to geometry that is currently selected
            if(this.loadedGeometrySelected !== null) {
                return loadedGeometryIndex !== this.loadedGeometrySelected;
            }
            return false
        },

        handleUndoLastEdit() {
            if (this.addByDrawingEnabled) {
                this.undoLastEdit(this.OTHER);
            } else {
                this.undoLastFeature();

            }
        },

        getAllProperties(item) {
            let allProperties = [];
            item.geometry.features.forEach(feature => {
                Object.keys(feature.properties).forEach(property => {
                    if (!this.excludedFields.includes(property) && !allProperties.includes(property)) {
                        allProperties.push(property);
                    }
                });
            });
            return allProperties
        },

        addNewPropertyToExisting(propertyName, propertyValue) {
            this.newProperties = {
                ...this.newProperties,
                [propertyName]: propertyValue !== undefined ? propertyValue : ""
            };
        },

        removeExistingProperty(propertyKey) {
            let updatedProperties = { ...this.newProperties };
            if (propertyKey in updatedProperties) {
                delete updatedProperties[propertyKey];
            }
            this.newProperties = updatedProperties
        },

        getLoadedGeometryType(loadedGeometry) {
            if (loadedGeometry.type) {
                switch (loadedGeometry.type) {
                    case 'Point':
                        return 'point';
                    case 'LineString':
                        return 'polyline';
                }
            } else if (loadedGeometry.geometry && loadedGeometry.geometry.features.length > 0) {
                const firstFeature = loadedGeometry.geometry.features[0];
                if (firstFeature.geometry && firstFeature.geometry.type) {
                    switch (firstFeature.geometry.type) {
                        case 'Point':
                            return 'point';
                        case 'LineString':
                            return 'polyline';
                    }
                }
            } else if(this.selectedLayers && this.selectedLayers[0]) {
                if(this.selectedLayers[0].layer.geometry.type) {
                    switch (this.selectedLayers[0].layer.geometry.type) {
                        case 'Point':
                            return 'point';
                        case 'LineString':
                            return 'polyline';
                    }
                }
            }
            return null;
        },

        getFeatureGeometryType(feature) {
            if (feature.geometry) {
                switch (feature.geometry.type) {
                    case 'Point':
                        return 'point';
                    case 'MultiPoint':
                        return 'point';
                    case 'LineString':
                        return 'polyline';
                }
            }
            return null;
        },

        getNextItemIndex() {
            let nextIndex = 0
            this.loadedGeometries.forEach(item => {
                if (item.importedItemIndex >= nextIndex) {
                    nextIndex = item.importedItemIndex + 1
                }
            })
            return nextIndex
        },

        getGeometryLength(geometry) {
            let length = 0
            if(geometry && geometry.features && geometry.features.length > 0) {
                geometry.features.forEach(feature => {
                    // LineString
                    if(feature && feature.geometry && feature.geometry.coordinates && feature.geometry.coordinates.length > 0
                        && feature.geometry.type === 'LineString') {
                        length += this.calculateLineStringLength(feature.geometry.coordinates)
                    }
                    // MultiLineString
                    if(feature.geometry.type === 'MultiLineString' && feature && feature.geometry &&
                        feature.geometry.coordinates && feature.geometry.coordinates.length > 0) {
                        feature.geometry.coordinates.forEach(coordinates => {
                            if (coordinates instanceof Array) {
                                length += this.calculateLineStringLength(coordinates)
                            }
                        })
                    }
                })
            }
            return length ? length : 0;
        },


        getLengthInKilometers(geometry, asNumber = false) {
            let lengthInKilometers = null
            let geometryLength = this.getGeometryLength(geometry)
            if(geometryLength > 0) {
                geometryLength = geometryLength / 1000
                geometryLength = geometryLength.toFixed(2)
                if (asNumber) {
                    lengthInKilometers = parseFloat(geometryLength)
                } else {
                    lengthInKilometers = geometryLength + ' ' + this.$t('contracts.unit_km')
                }
            }
            return lengthInKilometers
        },

        toggleStorages() {
            this.storagesEnabled = !this.storagesEnabled
        },

        showSelectionSelector() {
            let polygon = this.getPolygon() ? this.getPolygon() : null
            // select geometry items inside polygon
            this.selectedGeometryItems = this.selectGeometryAssets(this.loadedGeometries, polygon, this.isPointInPolygon)
            if(this.selectedGeometryItems.length > 0) {
                this.selectedGeometryItems.forEach(selectedItem => {
                    this.onGeoJsonClicked(selectedItem.featureItem, null, selectedItem.featureItem.properties.id, selectedItem.featureItem.properties.importedItemIndex)
                })
            }
            this.selectedGeometryItems = []
            this.clearPolygon()
            this.polygonDrawing = false
        },

        getPolygon: function () {
            // Outer ring needed only
            let rings = this.$refs.mapContainer.getMap().getPolygonBoundaries(this.newPolygonId, this.OTHER)
            if (rings.length > 0) {
                let result = rings[0].map(item => [item['lng'], item['lat']])
                // Must add last to equal first
                result.push([rings[0][0]['lng'], rings[0][0]['lat']])
                return result
            }
            return null
        },

        clearPolygon: function () {
            if (this.newPolygonId > 0) {
                this.$refs.mapContainer.getMap().removePolygon(this.newPolygonId, this.OTHER)
            }
            this.newPolygonId = -1
        },

        hidePolygonDrawing() {
            this.polygonDrawing = false
            this.clearPolygon()
        },

        enablePolygonDrawing() {
            this.newPolygonId = -1
            this.polygonDrawing = true
            toast.info(this.$t('map.select_assets_info'), {
                autoClose: 4000,
                position: toast.POSITION.BOTTOM_CENTER
            })
        },

        toggleContractAreas() {
            if(this.areasEnabled) {
                this.$refs.mapAreas.clearAreas()
            }
            this.areasEnabled = !this.areasEnabled
        },

        resetGeometry() {
            this.resetFeature()
            this.addRoadByClick = false
            this.loadedGeometrySelected = null
        },

        resetDrawing(resetLoadedGeometriesState = false) {
            // reset loaded geometries state if wanted and canceled
            this.map.stopEditingPolyline()
            if(this.preEditedGeometriesState !== null && resetLoadedGeometriesState) {
                this.loadedGeometries = this.preEditedGeometriesState
                // set selected layer as unselected after reset
                this.updateGeometryFeatureSelected(this.loadedGeometries, this.selectedLayers[0].layer.properties.id,
                    this.selectedLayers[0].layer.properties.importedItemIndex, false);
                this.updateGeoJsonMapObject(this.loadedGeometrySelected,  this.loadedGeometries[this.loadedGeometrySelected])
                this.updateLabel(this.loadedGeometrySelected,  this.loadedGeometries[this.loadedGeometrySelected].label)
            }
            this.resetGeometry()
            this.removeActiveFeature(this.OTHER)
            this.setRoadAddDisabled()
            this.selectedLayers = []
            this.addPolylineEnabled = false
            this.addPointEnabled = false
            this.loadedGeometrySelected = null
            this.newProperties = null
            this.showAddPropertiesModal = false
            this.preEditedGeometriesState = null
            this.polygonDrawing = false
        },

        setRoadAddEnabled(index) {
            this.resetDrawing()
            const sortedItem = this.sortGeometryAscending[index]
            this.loadedGeometrySelected = this.loadedGeometries.findIndex(g => g.importedItemIndex === sortedItem.importedItemIndex)
            this.addRoadByClick = true
        },

        toggleObservations() {
            this.observationsEnabled = !this.observationsEnabled
        },

        setRoadAddDisabled() {
            this.loadedGeometrySelected = null
            if(this.measureRoadEnabled) {
                this.$refs.measureRoad.reset()
            }
            this.addRoadByClick = false
            this.addRoadBySearch = false
            this.measureRoadEnabled = false
            this.geometryUpdates = 0
            // Also reset point/polyline adding states
            this.resetAllDrawingStates()
        },

        openRoadSearchWizard(index) {
            this.resetDrawing()
            this.addRoadBySearch = true
            const sortedItem = this.sortGeometryAscending[index]
            this.loadedGeometrySelected = this.loadedGeometries.findIndex(g => g.importedItemIndex === sortedItem.importedItemIndex)
            this.showRoadSearchWizard = true
        },

        openAddressSearchWizard(index) {
            this.resetDrawing()
            const sortedItem = this.sortGeometryAscending[index]
            this.loadedGeometrySelected = this.loadedGeometries.findIndex(g => g.importedItemIndex === sortedItem.importedItemIndex)
            this.showAddressSearchWizard = true
        },

        openRoadMeasurement(index) {
            this.resetDrawing()
            this.addRoadByClick = false
            const sortedItem = this.sortGeometryAscending[index]
            this.loadedGeometrySelected = this.loadedGeometries.findIndex(g => g.importedItemIndex === sortedItem.importedItemIndex)
            this.initMeasureRoad()
        },

        closeRoadMeasurement() {
            this.loadedGeometrySelected = null
            this.setRoadAddDisabled()
        },

        setNewGeometryFromMeasure(geometry, properties) {
            let polylines = geometry.map(item => [item['lon'], item['lat']])
            if(polylines && polylines.length > 0) {
                if (this.loadedGeometrySelected !== null) {
                    this.geoJsonToAdd = JSON.stringify(this.addFeatureCollection(polylines, null, null, properties))
                }
                this.setNewGeometryFromSearch(this.loadedGeometrySelected)
            }
        },

        setNewGeometryByDrawing(index, geometryType) {
            this.resetDrawing()
            this.addRoadByClick = false
            this.setEnabledFeature(geometryType)
            const sortedItem = this.sortGeometryAscending[index]
            this.loadedGeometrySelected = this.loadedGeometries.findIndex(g => g.importedItemIndex === sortedItem.importedItemIndex)
        },

        setNewGeometryFromSearch(importedItemIndex, focusOnLayer = false, pointGeometry = null) {
            if (this.geoJsonToAdd || this.hasPolylines || pointGeometry) {
                let geomObject = null
                if(this.geoJsonToAdd) {
                    geomObject = JSON.parse(this.geoJsonToAdd)
                } else if(this.hasPolylines) {
                    geomObject = this.addFeatureCollection(this.getPolyline(this.OTHER))
                }
                if (this.loadedGeometrySelected !== null) {
                    let filteredGeometry = this.loadedGeometries[this.loadedGeometrySelected];
                    if (filteredGeometry && filteredGeometry.geometry && filteredGeometry.geometry.features && filteredGeometry.geometry.features.length > 0) {
                        let feature = this.addFeature(pointGeometry ? pointGeometry : geomObject.features[0].geometry.coordinates, filteredGeometry.geometry.features.length,
                            filteredGeometry.importedItemIndex, pointGeometry ? null : geomObject.features[0].properties, pointGeometry ? 'point' : 'polyline')
                        filteredGeometry.geometry.features.push(feature)
                    } else {
                        let properties = geomObject.features[0].properties ? geomObject.features[0].properties : {addedOn: new Date().getTime()}
                        filteredGeometry.geometry = this.addFeatureCollection(pointGeometry ? pointGeometry : geomObject.features[0].geometry.coordinates, 0,
                            filteredGeometry.importedItemIndex, properties, pointGeometry ? 'point' : 'polyline')
                    }
                    // indicate that there are updates to geometries
                    this.geometryUpdates++
                    this.updateGeoJsonMapObject(this.loadedGeometrySelected, filteredGeometry)
                } else {
                    this.handleGeoJson(JSON.stringify(geomObject))
                }
                if (!this.addRoadByClick && !this.addRoadBySearch && !this.measureRoadEnabled) {
                    // Keep the road selection mode enabled
                    this.loadedGeometrySelected = null
                }
                this.geoJsonToAdd = null
                this.showRoadSearchWizard = false
                this.showAddressSearchWizard = false
                this.addPolylineEnabled = false
                this.geometryModified = true
                this.removeActiveFeature(this.OTHER)
                if(this.$refs.measureRoad) {
                    this.$refs.measureRoad.reset()
                }
            }
        },


        addPropertiesToGeometry(editMode = false, selectedLayer = null) {
            const geometry = this.loadedGeometrySelected >= 0 ? this.loadedGeometries[this.loadedGeometrySelected] : null;
            if (geometry) {
                let properties = {};
                if (this.hasGeometryProperties(geometry)) {
                    if(selectedLayer !== null) {
                        properties = selectedLayer.layer.properties
                    } else {
                        properties = geometry.geometryProperties.reduce((obj, prop) => {
                            obj[prop] = "";
                            return obj;
                        }, {});
                    }
                } else if (selectedLayer !== null) {
                    properties = selectedLayer.layer.properties
                } else if(this.hasPropertiesInFeature(geometry)) {
                    properties = Object.keys(geometry.geometry.features[0].properties).reduce((acc, key) => {
                        acc[key] = editMode ? geometry.geometry.features[0].properties[key] : "";
                        return acc;
                    }, {});
                }
                this.newProperties = properties;
                this.showAddPropertiesModal = true;
            }
        },

        addToGeometry() {
            let geometry = null
            if(this.addPolylineEnabled) {
                geometry = this.map.getEditablePolylineCoordinates()
            } else if(this.addPointEnabled) {
                geometry = this.getPoint()
            }
            if (geometry) {
                if (this.loadedGeometrySelected !== null) {
                    let filteredGeometry = this.loadedGeometries[this.loadedGeometrySelected];
                    let geometryType = this.getLoadedGeometryType(filteredGeometry)
                    if (filteredGeometry && filteredGeometry.geometry) {
                        let feature = null
                        if(this.showAddPropertiesModal && this.selectedLayers && this.selectedLayers[0]) {
                            feature = this.addExistingFeature(geometry, this.newProperties, geometryType)
                            feature.properties.selected = false
                        } else {
                            feature = this.addFeature(geometry, filteredGeometry.geometry.features.length, filteredGeometry.importedItemIndex, this.newProperties, geometryType)
                        }
                        filteredGeometry.geometry.features.push(feature)
                    } else {
                        filteredGeometry.geometry = this.addFeatureCollection(geometry, 0, filteredGeometry.importedItemIndex, this.newProperties, geometryType)
                    }
                    this.updateGeoJsonMapObject(this.loadedGeometrySelected, filteredGeometry)
                    this.updateLabel(this.loadedGeometrySelected, filteredGeometry.label)

                    // Check if we're in editing mode (preEditedGeometriesState is set)
                    const isEditingMode = this.preEditedGeometriesState !== null;

                    // Cleanup based on mode
                    if (isEditingMode) {
                        this.completeEditMode()
                    } else if (geometryType === 'point') {
                        this.resetPointForNext()
                    } else {
                        this.loadedGeometrySelected = null
                    }
                } else {
                    if(this.selectedLayers && this.selectedLayers.length > 0) {
                        let filteredGeometry = this.loadedGeometries.find(geometry => geometry.importedItemIndex === this.selectedLayers[0].importedItemIndex);
                        let geometryType = this.getFeatureGeometryType(this.selectedLayers[0].layer)
                        let feature = this.addExistingFeature(geometry, this.selectedLayers[0].layer.properties, geometryType)
                        feature.properties.selected = false
                        filteredGeometry.geometry.features.push(feature)

                        // TODO - Probably need to update here too the layers? How do we end up here???

                    } else {
                        let geometry = this.addFeatureCollection(geometry, null, null, null, this.addPolylineEnabled ? 'polyline' : 'point')
                        this.handleGeoJson(JSON.stringify(geometry))
                    }
                }
                this.geometryModified = true

                // Only call resetDrawing if not in editing mode
                if(this.preEditedGeometriesState === null && !this.addPointEnabled) {
                    this.resetDrawing()
                }
            }
        },

        completeEditMode() {
            this.preEditedGeometriesState = null
            this.selectedLayers = []
            this.loadedGeometrySelected = null

            this.$nextTick(() => {
                this.removeActiveFeature(this.OTHER)
                this.resetAllDrawingStates()
            })
        },

        resetPointForNext() {
            if (this.newPointId > 0) {
                this.map.removeMapMarker(this.newPointId, this.OTHER)
                this.newPointId = -1
            }
            this.newProperties = null
            this.showAddPropertiesModal = false
        },

        resetAllDrawingStates() {
            this.addPointEnabled = false
            this.addPolylineEnabled = false
            this.activeFeature = ""
            this.drawnFigure = false
            this.newProperties = null
            this.showAddPropertiesModal = false
        },

        hasGeometryProperties(loadedGeometry) {
            return loadedGeometry && loadedGeometry.geometryProperties && loadedGeometry.geometryProperties.length > 0
        },

        hasPropertiesInFeature(loadedGeometry) {
            if (!loadedGeometry || !loadedGeometry.geometry || loadedGeometry.geometry.features.length === 0) {
                return false;
            }
            const properties = loadedGeometry.geometry.features[0].properties;
            if (!properties) {
                return false;
            }
            for (const prop in properties) {
                if (properties.hasOwnProperty(prop) && prop !== 'importedItemIndex' && prop !== 'id') {
                    return true;
                }
            }
            return false;
        },

        editGeometry(geometryType) {
            this.loading = true
            setTimeout(() => {
                this.setEnabledFeature(geometryType)
                this.preEditedGeometriesState= JSON.parse(JSON.stringify(this.loadedGeometries))
                this.filterLoadedGeometries()
                let selectedLayer = this.selectedLayers[0]
                if(this.selectedLayers && this.selectedLayers.length > 0) {
                    this.loadedGeometrySelected = this.loadedGeometries.findIndex(geometry => geometry.importedItemIndex === selectedLayer.importedItemIndex);
                    this.addPropertiesToGeometry(true, selectedLayer)
                    this.selectedLayers.forEach(selectedLayer => {
                        if (selectedLayer.layer && selectedLayer.layer.geometry
                            && selectedLayer.layer.geometry.coordinates && selectedLayer.layer.geometry.coordinates.length > 0) {
                            this.updateLabel(this.loadedGeometrySelected, false)
                            if (geometryType === 'polyline') {
                                this.map.addEditablePolyline(selectedLayer.layer.geometry.coordinates)
                            } else if (geometryType === 'point' && selectedLayer.layer.geometry.coordinates[1] && selectedLayer.layer.geometry.coordinates[0]) {
                                this.drawPoint(this.OTHER, {
                                    lat: selectedLayer.layer.geometry.coordinates[1],
                                    lng: selectedLayer.layer.geometry.coordinates[0]
                                })
                            }
                        }
                    })
                }
                this.loading = false
            }, 50) // Slightly longer timeout for cleanup
        },

        closeGeometryModal() {
            this.showGeometryModal = false;
            this.isEditingGeometry = false;
            this.currentGeometry = {}
        },
        saveGeometry(updatedGeometry) {
            if (this.isEditingGeometry) {
                // Handle updating existing geometry
                const index = this.loadedGeometries.findIndex(g => g.id === updatedGeometry.id);
                if (index !== -1) {
                    this.loadedGeometries[index] = updatedGeometry;
                    this.updateGeoJsonMapObject(index, updatedGeometry)
                }
            } else {
                // Handle adding new geometry
                const newGeometry = {
                    name: updatedGeometry.name,
                    label: null,
                    geometry: null,
                    color: updatedGeometry.color ? updatedGeometry.color : "#B200FF",
                    type: updatedGeometry.type,
                    geometryProperties: updatedGeometry.properties,
                    calculate: false,
                    visibility: true,
                    lineWidth: 2,
                    addPolylineEnds: false,
                    showOrder: this.loadedGeometries.length + 1,
                    importedItemIndex: this.getNextItemIndex()
                }
                this.loadedGeometries.push(newGeometry)
                this.map.addGeoJsonLayer(
                    JSON.stringify(newGeometry.geometry), false, newGeometry.color, newGeometry.addPolylineEnds,
                    newGeometry.transparency ? newGeometry.transparency : 1,
                    newGeometry.lineWidth ? newGeometry.lineWidth : 2, newGeometry.visibility
                )
                this.geometryModified = true
            }
            this.closeGeometryModal();
        },

        openEditGeometryModal(loadedGeometry) {
            this.isEditingGeometry = true;
            this.currentGeometry = { ...loadedGeometry };
            this.showGeometryModal = true;
        },

        openDownloadGeometryModal(item) {
            this.showDownloadAsFileModal = true
            this.geometriesForDownload = item
        },

        closeDownloadGeometryModal() {
            this.showDownloadAsFileModal = false
            this.geometriesForDownload = null
        },

        updateGeometryFeatureSelected(array, index, importedItemIndex, value) {
            array.some(loadedGeometry => {
                return loadedGeometry.geometry.features.some((feature) => {
                    if (feature.properties.id === index && feature.properties.importedItemIndex === importedItemIndex) {
                        feature.properties.selected = value;
                        return true;
                    }
                });
            });
        },

        updateAllLabelPositions() {
            if(this.loadedGeometries && this.loadedGeometries.length > 0) {
                for (let i = 0; i < this.loadedGeometries.length; i++) {
                    let visibility = this.loadedGeometries[i] && this.loadedGeometries[i].visibility
                    this.updateLabel(i, visibility)
                }
            }
        },

        findGeometryFeature(array, index, importedItemIndex) {
            let foundFeature;
            array.some(loadedGeometry => {
                return loadedGeometry.geometry.features.some(feature => {
                    if (feature.properties.id === index && feature.properties.importedItemIndex === importedItemIndex) {
                        foundFeature = {feature: feature, loadedGeometry: loadedGeometry};
                        return true;
                    }
                });
            });
            return foundFeature;
        },

        clearGeometrySelection() {
            this.loadedGeometries.forEach(layer => {
                if (layer && layer.geometry) {
                    layer.geometry.features.forEach(geometry => {
                        geometry.properties.selected = false
                    })
                }
            })
            this.selectedLayers = []
        },

        undoLastFeature() {
            if(this.loadedGeometrySelected !== null) {
                let selectedGeometry = this.loadedGeometries[this.loadedGeometrySelected]
                if(selectedGeometry && selectedGeometry.geometry && selectedGeometry.geometry.features && selectedGeometry.geometry.features.length > 0 && this.geometryUpdates > 0) {
                    selectedGeometry.geometry.features.splice(selectedGeometry.geometry.features.length - 1, 1);
                    this.geometryUpdates--
                    this.updateGeoJsonMapObject(this.loadedGeometrySelected, selectedGeometry)
                }
            }
        },

        onGeoJsonClicked(feature, layer, index, importedItemIndex) {
            if(this.addRoadByClick || this.addRoadBySearch || this.addByDrawingEnabled || this.measureRoadEnabled) {
                return
            }
            let layersUnSelected = false
            // check if there are layers selected
            if(this.selectedLayers.length > 0) {
                // ... loop through them
                 this.selectedLayers.forEach((selectedLayer, idx) => {
                    // check if we click a layer that is already selected on map
                    if(selectedLayer.importedItemIndex === importedItemIndex && selectedLayer.index === index) {
                        // unselect already selected layer by looping through features
                        this.updateGeometryFeatureSelected(this.loadedGeometries, index, importedItemIndex, false);
                        // delete that layer from selectedLayers array
                        this.selectedLayers.splice(idx, 1)
                        // set layerUnSelected value true so selection function is done
                        layersUnSelected = true

                    }
                })
            }
            // if user clicked a layer that is not selected then set it as selected
            if(!layersUnSelected && importedItemIndex !== undefined && index !== undefined) {
                const foundItem = this.findGeometryFeature(this.loadedGeometries, index, importedItemIndex);
                let geometryLayer = {  ...foundItem.loadedGeometry,
                    geometry: {...foundItem.loadedGeometry.geometry, features: [foundItem.feature] },
                    }
                // add selected layer to selectedLayer array with its ids and objectIndex
                this.selectedLayers = [...this.selectedLayers, {
                    layer: foundItem.feature,
                    index: index,
                    importedItemIndex: importedItemIndex,
                    loadedGeometry: geometryLayer
                }];
                // set loadedGeometries property selected true so map highlights it as selected
                let geometryType = this.getFeatureGeometryType(this.selectedLayers[0].layer)
                if(geometryType === 'point' && !this.polygonDrawing) {
                    this.editGeometry(geometryType)
                    return  // Exit early, don't refresh map, quick fix for refreshing after editing causes problems
                } else {
                    this.updateGeometryFeatureSelected(this.loadedGeometries, index, importedItemIndex, true);
                }

            }
            // refresh map
            // Delay map refresh to allow event handlers to complete
            this.$nextTick(() => {
                this.hideImportedObjects()
                this.addGeoJsonMapObjects(false, false)
            })
        },

        filterLoadedGeometries() {
            // loop through all loadedGeometries and filter them by selectLayers
            for (let i = 0; i < this.loadedGeometries.length; i++) {
                if(this.loadedGeometries[i].geometry && this.loadedGeometries[i].geometry.features) {
                    this.loadedGeometries[i].geometry.features = this.loadedGeometries[i].geometry.features.filter((feature) => {
                        let shouldKeepFeature = true;
                        this.selectedLayers.forEach((fd) => {
                            if (fd.index === feature.properties.id && fd.importedItemIndex === feature.properties.importedItemIndex) {
                                shouldKeepFeature = false;
                            }
                        });
                        return shouldKeepFeature;
                    });
                }
            }
        },

        removeGeometryPaths() {
            if (this.selectedLayers.length > 0) {
                if(this.activeFeature === 'point') {
                    this.resetDrawing()
                }
                this.filterLoadedGeometries()
                // set selectedLayers to removedGeometries so user can restore them if needed
                this.removedGeometries = this.selectedLayers
                // set selected layer empty
                this.selectedLayers = []
                this.geometryModified = true
                // render map
                this.hideImportedObjects()
                this.addGeoJsonMapObjects(false)
            }
        },

        restoreGeometryPath() {
            // add geojson layer to map
            // check if there are removed geometries
            if (this.removedGeometries.length > 0) {
                // get the latest removed layer from removed geometries
                const removedLayer = this.removedGeometries.pop()
                // check if removed layer is true and has needed content in it
                if(removedLayer && removedLayer.layer) {
                    // set removed layer selected value false that way it is not selected on map when restored
                    removedLayer.layer.properties.selected = false
                    // find the geometry index where the feature was deleted
                    const findImportedItemIndex = (array, importedItemIndex) => {
                        let foundIndex;
                        array.some((loadedGeometry,index) => {
                            if(loadedGeometry.importedItemIndex === importedItemIndex) {
                                foundIndex = index
                                return true
                            }
                        });
                        return foundIndex;
                    };
                    const foundIndex = findImportedItemIndex(this.loadedGeometries, removedLayer.importedItemIndex)
                    // set removed layer back to loadedGeometries to its right place
                    if(typeof foundIndex !== undefined) {
                        this.loadedGeometries[foundIndex].geometry.features.forEach((feature,index) => {
                            feature.properties.id = index
                        })
                        removedLayer.layer.id = this.loadedGeometries[foundIndex].geometry.features.length
                        this.loadedGeometries[foundIndex].geometry.features.push(
                            removedLayer.layer
                        )
                    }
                    this.geometryModified = true
                }
                // refresh map
                this.hideImportedObjects()
                this.addGeoJsonMapObjects(false)
            }
        },

        setColor(color) {
            this.newColor = color
        },

        selectColor(color, index) {
            this.loadedGeometries[index] = {
                ...this.loadedGeometries[index],
                color: color
            }
            this.geometryModified = true
            this.updateGeoJsonMapObject(index, this.loadedGeometries[index])
        },

        onZoomLevelChange(data) {
            if (!this.geometries) {
                this.$store.commit('storeMapZoomLevel', data)
            }
            this.updateAllLabelPositions()
        },

        onDrag: function (data) {
            if (this.measureDistanceEnabled) {
                this.updateMeasureDistance(data)
                this.geometryModified = true
            }
        },

        onDragEnd: function (data) {
            if (this.measureDistanceEnabled) {
                this.updateMeasureDistance(data)
                this.geometryModified = true
            }
            if (this.measureRoadEnabled) {
                this.$refs.measureRoad.addMeasurePoint(data)
            }
        },

        onMarkerTap(data) {
            switch (data.type) {
                case this.OBSERVATION:
                    this.showObservationDetails(data)
                    break
                case this.MATERIAL_STORAGE:
                    this.editStorage = JSON.parse(JSON.stringify(this.$refs.storages.getStorage(data.id)))
                    break
            }
        },

        showObservationDetails: function (data) {
            if (this.observationsEnabled) {
                this.$refs.observations.onMarkerTap(data)
            }
        },

        closeAndUpdateStorage() {
            this.editStorage = null
            this.$refs.storages.updateStorages(true)
        },

        mapClicked: function (coord) {
            this.addPoints(this.OTHER, coord)
            if(this.addRoadByClick) {
                this.addGeometryFromClick(coord)
            }
            if (this.measureDistanceEnabled) {
                this.$refs.measureDistances.addPoint(coord)
            }
            if (this.measureRoadEnabled) {
                this.$refs.measureRoad.addMeasurePoint(coord)
            }
            if(this.polygonDrawing) {
                this.addPointToPolygon(coord)
            }
        },

        addPointToPolygon: function (point) {
            // If not exist yet, create one and add to map
            if (this.newPolygonId < 0) {
                this.newPolygonId = Date.now()
                this.$refs.mapContainer.getMap().newPolygon(this.newPolygonId, point.lat, point.lng, this.OTHER)

            } else {
                this.$refs.mapContainer.getMap().addPointToPolygon(this.newPolygonId, point.lat, point.lng, this.OTHER)
            }
        },

        pointerCoordinateMove(coord) {
            this.mouseCoordinate = coord
        },

        onGeoJsonHoverEvent(feature, layer,) {
            this.showLabel(layer, 'roadNameLabel', this.mouseCoordinate)
        },

        onGeoJsonHoverExitEvent() {
            this.map.removeGeoLabels('roadNameLabel')
        },

        addGeometryFromClick(coord) {
            this.fetchRoad(coord)
        },

        mapChanged(map) {
            this.map = map
            // Set center
            const hasGeometries = this.geometries && this.geometries.length > 0
            if(!hasGeometries) {
                if(this.getMapSettingsIn(['center'])){
                    const center = this.getMapSettingsIn(['center'])
                    this.map.zoomToPosition(center.lat, center.lng, this.map.getMapZoomLevel())
                } else if (this.user && this.user.company.location) {
                    this.map.zoomToPosition(this.user.company.location.y, this.user.company.location.x, this.map.getMapZoomLevel())
                }
            }
            else if(hasGeometries) {
                // Try to show the map as soon as possible
                // Time indexing to next event loop, so it won't block the UI in this event loop
                setTimeout(() =>   {
                    this.indexGeometries(this.geometries)
                    this.addGeoJsonMapObjects(true, false)
                }, 20)
            }
        },
        hideImportedObjects: function () {
            this.map.removeGeoJsonLayers()
            this.map.removeAllLineEndIndicators()
        },
        handleGeoJson: function(file, name){
            if (file) {
                let geomObject = JSON.parse(file)
                // set artificial ids and indexes when new geojson is added that way we can manipulate it on map
                geomObject.features.forEach((feature, index) => {
                    feature.properties.id = index
                    feature.properties.importedItemIndex = this.loadedGeometries.length
                })
                const newGeometry = {
                    name: name ? name : this.shpFile ? this.shpFile.name : `uusi_tiegeometria(${this.loadedGeometries.length}).shp`,
                    label: null,
                    geometry: geomObject,
                    color: '#b200ff',
                    calculate: false,
                    visibility: true,
                    lineWidth: 2,
                    showOrder: this.loadedGeometries.length + 1,
                    addPolylineEnds: false,
                    importedItemIndex: this.loadedGeometries.length
                }
                this.loadedGeometries.push(newGeometry)
                this.map.addGeoJsonLayer(
                    JSON.stringify(newGeometry.geometry), false, newGeometry.color, newGeometry.addPolylineEnds,
                     1, newGeometry.lineWidth ? newGeometry.lineWidth : 2, newGeometry.visibility
                )

                this.importFromFile = false
                this.geometryModified = true
            }
            this.loading = false
            this.zoomToGeometry(this.loadedGeometries[this.loadedGeometries.length - 1])
        },
        handleVisibilityChange(index) {
            let layer
            this.loadedGeometries = this.loadedGeometries.map((item, i) => {
                if (i == index) {
                    item.visibility = !item.visibility
                    layer = item
                    return item
                }
                return item
            });
            this.loading = true
            // Spinner shows only with timeout
            setTimeout(() => {
                if (layer) {
                    this.updateLabel(index, layer.visibility)
                }
                this.map.toggleGeoJsonVisibility(index)
                this.loading = false
            }, 20)
        },
        addGeoJsonFromSearchToMap: function (geoJson) {
            this.geoJsonToAdd = geoJson
            this.setNewGeometryFromSearch(this.loadedGeometrySelected, true)
        },
        addPointFromSearchToMap(coord) {
            if(coord) {
                let coordinate = [coord.lon, coord.lat]
                this.showAddressSearchWizard = false
                this.setNewGeometryByDrawing(this.loadedGeometrySelected, 'point')
                this.drawPoint(this.OTHER, {lat: coordinate[1], lng: coordinate[0]})
                this.geometryModified = true
            }

        },
        showCopyOrderGeometryModal: function () {
            this.showExportGeometriesModal = true
        },
        doCopyOrderGeometry: function (selectedOrderIds) {
            this.loading = true
            let geometries = JSON.parse(JSON.stringify(this.exportGeometries))
            if (geometries) {
                let convertedGeometries = this.convertGeometriesForServer(geometries)
                convertedGeometries.forEach(item => {
                    item.geometry = typeof item.geometry === 'object' && item.geometry !== null ? JSON.stringify(item.geometry) : item.geometry
                })
                this.restUpdate(this.orderGeometriesUrl + '/copy', {orders: selectedOrderIds, geometries: convertedGeometries},
                    this.exportSuccess, this.fail)
            }
        },
        cancelCopying() {
            this.showExportGeometriesModal = false
            this.exportGeometries = []
        },
        exportSuccess: function () {
            this.loading = false
            if (this.$refs.exportModal?.isDeleteSelected()) {
                this.deleteGeometriesByPolygon()
            }
            this.showExportGeometriesModal = false
            this.exportGeometries = []
            this.showAlert(this.$t('orders.geometry_export_successful'), 'success')
        },
        fail: function () {
            this.showAlert(this.$t('common.alert_update_failed'))
            this.showExportGeometriesModal = false
            this.loading = false
        },
        downloadShape: function (geometryLayer, targetCoordinateSystem) {
            this.loading = true
            this.restPostDownloadFile(this.convertGeoJsonToShapeUrl, {geometry_layer: geometryLayer, target_coordinate_system: targetCoordinateSystem}, this.downloadAsFileSuccess, this.fail)
        },
        downloadAsFileSuccess: function (response) {
            const fileName = this.geometriesForDownload ? this.geometriesForDownload.name + '.zip' : this.$t('orders.shapefile_default_name')
            this.loading = false
            this.downloadZipFile(response, fileName)
            this.geometriesForDownload = false
        },
        fetchRoad(coord) {
            this.loading = true
            this.restFetchParams(this.roadGeometryLocationUrl, {
                lat: coord.lat,
                lon: coord.lng,
                geoJson: 1,
            }, this.handleResponse, this.onError)
        },

        handleResponse: function (response) {
            this.loading = false
            if (response && response.data) {
                let feature = response.data.features[0]
                if (this.loadedGeometrySelected !== null) {
                    this.geoJsonToAdd = JSON.stringify(this.addFeatureCollection(feature.geometry.coordinates, null, null, feature.properties))
                }
                this.setNewGeometryFromSearch(this.loadedGeometrySelected)
            }
        },
        onFailedRequest: function(){
            this.loading = false
            this.showAlert(this.$t('import_file_wizard.import_failed'))
        },

        async updateGeoJsonMapObject(index, item) {
            this.loading = true
            setTimeout(() => {
                // First remove line end indicators
                this.map.removeAllLineEndIndicators()
                // Handle layers
                const sortedIndex = this.sortGeometryAscending.findIndex(g => g.importedItemIndex === item.importedItemIndex)
                this.map.updateGeoJsonLayer(sortedIndex, JSON.stringify(item.geometry), false, item.color, item.addPolylineEnds,
                    item.transparency ? item.transparency : 1, item.lineWidth ? item.lineWidth : 2,
                    item.visibility)
                this.loading = false
            }, 20)
        },

        addGeoJsonMapObjects(focusOnLayer = true, updateLabel = true) {
            this.loading = true
            const processGeometries = () => {
                this.sortGeometryAscending.forEach((item, index) => {
                    if(updateLabel) {
                        this.updateLabel(index, item.visibility)
                    }

                    // Apply highlighting if needed
                    let color = item.color;
                    let lineWidth = item.lineWidth || 2;
                    let transparency = item.transparency || 1;

                    if (item.highlighted) {
                        color = '#00FFFF'; // Bright cyan
                        lineWidth = lineWidth + 3; // Thicker
                        transparency = 1; // Full opacity
                    }

                    this.map.addGeoJsonLayer(JSON.stringify(item.geometry), false, color, item.addPolylineEnds,
                        transparency, lineWidth, item.visibility)
                })
                if (focusOnLayer) {
                    this.$nextTick(() => {
                        this.map.zoomToGeoJsons()
                    })
                }
            }
            this.$nextTick(() => processGeometries())
            setTimeout(() => this.loading = false, 500)
        },
        handleSaveClick() {
            if(this.addPointEnabled && this.newPointId > 0) {
                // For points, show properties modal
                this.addPropertiesToGeometry()
            } else {
                // For other geometries, use existing logic
                if((this.preEditedGeometriesState === null && this.hasPropertiesInFeature(this.loadedGeometries[this.loadedGeometrySelected])) || this.hasGeometryProperties(this.loadedGeometries[this.loadedGeometrySelected])) {
                    this.addPropertiesToGeometry()
                } else {
                    this.addToGeometry()
                }
            }
        },
        emitGeometry() {
            // Clear selections
            this.clearGeometrySelection()
            // Ensure there are no empty layers
            if (this.hasEmptyLayers()) {
                this.showAlert(this.$t('orders.error_empty_layer'))
            } else if (this.loadedGeometries) {
                // Emit as json string
                let results = this.convertGeometriesForServer(this.loadedGeometries)
                this.$emit('geometrySelected', results, this.geometryModified)
            }
        },
        indexGeometries(geometries) {
            this.loadedGeometries = []
            let start = this.getHighestArrayValue(geometries, "show_order")
             geometries.forEach((item, index) => {
                // make a true copy of the geometry not just referencing it that way we don't mutate props values
                let changedGeometry = JSON.parse(JSON.stringify(item.geometry))
                // insert temporary ids and object indexes so selection can be possible
                changedGeometry.features.forEach((feature, geomIndex) => {
                    // Convert array to object if feature.properties is empty
                    if (feature.properties && Array.isArray(feature.properties) && feature.properties.length === 0) {
                        feature.properties = {};
                    }

                    // Add new properties
                    feature.properties.id = geomIndex;
                    feature.properties.importedItemIndex = index;
                });
                this.loadedGeometries.push({
                    id: item.id,
                    name: item.name ? item.name : ('Geom' + (index + 1)),
                    label: item.label,
                    geometry: changedGeometry,
                    addPolylineEnds: item.addPolylineEnds ? item.addPolylineEnds : item.add_polyline_ends ? item.add_polyline_ends : false,
                    calculate: item.calculate,
                    color: item.color ? item.color : '#b200ff',
                    lineWidth: item.lineWidth ? item.lineWidth : item.line_width ? item.line_width : 2,
                    visibility: item.visibility ? item.visibility : false,
                    showOrder: !item.show_order ? this.getNumber(start) + 1 : item.show_order,
                    importedItemIndex: index
                });
            })
        },
        getNumber(start) {
            const showOrder = this.getHighestArrayValue(this.loadedGeometries, "showOrder")
            return start < showOrder ? showOrder : start
        },

        cutDecimals(coordinates) {
            if (Array.isArray(coordinates[0])) {
                return coordinates.map(innerArray => this.cutDecimals(innerArray));
            } else {
                // Parse float once to round to desired precision - toFixed returns string, so need to parsefloat again to have the final value as double
                return coordinates.map(coord => parseFloat(parseFloat(coord).toFixed(7)));
            }
        },

        processFeatureCoordinates(features) {
            features.forEach((feature) => {
            feature.geometry.coordinates = this.cutDecimals(feature.geometry.coordinates);
            });
        },

        convertGeometriesForServer(geometries) {
            let results = []
            geometries.forEach((item, index) => {
                let name = item.name ? item.name : ('Geom' + (index+1))
                let label = item.label
                // add geometry where features are not empty
                if(item.geometry.features.length > 0) {
                    if(item.hasOwnProperty('geometryProperties')) {
                        delete item.geometryProperties
                    }
                    let changedGeometry = item.geometry
                    this.processFeatureCoordinates(changedGeometry.features);
                    changedGeometry.features.forEach((feature) => {
                        if(feature.properties.hasOwnProperty('id')) {
                            delete feature.properties.id
                        }
                        if(feature.properties.hasOwnProperty('importedItemIndex')) {
                            delete feature.properties.importedItemIndex
                        }
                    });
                    results.push({
                        name: name,
                        label: label,
                        geometry: changedGeometry,
                        add_polyline_ends: item.addPolylineEnds ? item.addPolylineEnds : false,
                        calculate: item.calculate ? item.calculate : false,
                        visibility: item.visibility ? item.visibility : false,
                        color: item.color ? item.color : '#b200ff',
                        show_order: item.showOrder,
                        line_width: item.lineWidth ? item.lineWidth : 2
                    })
                }
            })
            return results
        },
        importNewGeometry() {
            this.shpFile = null
            this.shxFile = null
            this.dbfFile = null
            this.importFromFile = true
            this.geometryModified = true
        },
        addLayerFromGeoJson(geoJson, name) {
            this.handleGeoJson(geoJson, name)
        },
        openRemoveGeometryModal(item, index) {
            this.removableIndex = index;
            this.removableItem = item;
            this.deleteGeometryModalOpen = true;
        },
        confirmRemoveGeometry() {
            this.removeGeometry(this.removableItem, this.removableIndex);
            this.deleteGeometryModalOpen = false;
        },
        closeConfirmRemoveGeometryAlert() {
            this.removableIndex = null;
            this.removableItem = null;
            this.deleteGeometryModalOpen = false;
        },
        removeGeometry(item, index) {
            this.map.removeGeoLabels(this.loadedGeometries[index].name)
            this.loadedGeometries.splice(index, 1)
            this.geometryModified = true
            this.map.removeGeoJsonLayer(index)
            this.removableItem = null
            this.removableIndex = null
        },
        deleteGeometriesByPolygon() {
            this.loading = true
            setTimeout(() => {
                if(this.loadedGeometries && this.loadedGeometries.length > 0) {
                    let newGeometries = []
                    this.loadedGeometries.forEach((item) => {
                        if (item.visibility) {
                            let manipulatedGeo = this.getOutsideParts(this.getPolygon(), item.geometry)
                            if (manipulatedGeo && manipulatedGeo.features && manipulatedGeo.features.length > 0) {
                                // Update ids and geometries
                                manipulatedGeo.features.forEach((feature, index) => feature.properties.id = index + 1)
                                item.geometry = manipulatedGeo
                                newGeometries.push(item)
                            }
                        } else {
                            // Nothing to do with invisible, keep as is
                            newGeometries.push(item)
                        }
                    })
                    this.loadedGeometries = newGeometries
                    this.hideImportedObjects()
                    this.addGeoJsonMapObjects(false)
                }
                this.clearPolygon()
                this.polygonDrawing = false
                this.loading = false
            }, 20)
        },
        copyAllGeometries() {
            this.$store.commit('storeCopiedGeometries', JSON.stringify(this.loadedGeometries))
            this.showAlert(this.$t('orders.geometry_copy_successful'), 'success')
        },
        copySelectedGeometry() {
            let copiedLayers = this.getCopiedSelectedLayers()
            if(copiedLayers && copiedLayers.length > 0) {
                this.$store.commit('storeCopiedGeometries', JSON.stringify(copiedLayers))
                this.showAlert(this.$t('orders.geometry_copy_successful'), 'success')
            }
        },
        copyInsidePolygon() {
            let copiedLayers = this.getCopiedLayersInsidePolygon()
            if(copiedLayers && copiedLayers.length > 0) {
                this.$store.commit('storeCopiedGeometries', JSON.stringify(copiedLayers))
                this.showAlert(this.$t('orders.geometry_copy_successful'), 'success')
            }
        },
        exportSelectedGeometry() {
            this.exportGeometries = this.getCopiedSelectedLayers()
            if(this.exportGeometries && this.exportGeometries.length > 0) {
                this.showCopyOrderGeometryModal()
            }
        },
        exportGeometryInsidePolygon() {
            this.exportGeometries = this.getCopiedLayersInsidePolygon()
            if(this.exportGeometries && this.exportGeometries.length > 0) {
                this.showCopyOrderGeometryModal()
            }
        },
        exportAllGeometry() {
            this.exportGeometries = JSON.parse(JSON.stringify(this.loadedGeometries))
            if(this.exportGeometries && this.exportGeometries.length > 0) {
                this.showCopyOrderGeometryModal()
            }
        },
        getCopiedSelectedLayers() {
            let copiedLayers = [];
            if(this.selectedLayers && this.selectedLayers.length > 0) {
                this.selectedLayers.forEach(selectedLayer => {
                    if(selectedLayer && selectedLayer.loadedGeometry) {
                        copiedLayers.push(selectedLayer.loadedGeometry)
                    }
                })
            }
            return copiedLayers
        },
        getCopiedLayersInsidePolygon() {
            let copiedLayers = [];
            this.loadedGeometries.forEach(item => {
                if (item.visibility) {
                    let copiedItem = JSON.parse(JSON.stringify(item))
                    let manipulatedGeo = this.getInsidePolygon(this.getPolygon(), item.geometry)
                    if (manipulatedGeo) {
                        copiedItem.geometry = manipulatedGeo
                        copiedLayers.push(copiedItem)
                    }
                }
            });
            return copiedLayers
        },
        pasteGeometry() {
            let geometries = JSON.parse(this.$store.state.copiedGeometries);
            geometries.forEach(element => {
                let found = this.loadedGeometries.find(item =>
                    item.hasOwnProperty('name') &&
                    element.hasOwnProperty('name') &&
                    item.name === element.name
                );

                if (found) {
                    // If geometry with the same name is found, append features to the existing geometry
                    element.geometry.features.forEach((feature, index) => {
                        // Ensure unique IDs for each feature added
                        feature.properties.id = found.geometry.features.length + index;
                        feature.properties.importedItemIndex = found.importedItemIndex;
                        found.geometry.features.push(feature);
                    });
                } else {
                    // If geometry is not found, proceed with your existing logic
                    let counter = 1;
                    while (found) {
                        const lastIndexSeparator = element.name.lastIndexOf('_');
                        if (lastIndexSeparator > 0) {
                            // Check if last part is a number
                            const trailer = element.name.substring(lastIndexSeparator + 1);
                            if (!isNaN(trailer)) {
                                // Update counter and remove trailer
                                counter = parseInt(trailer, 10) + 1;
                                element.name = element.name.substring(0, lastIndexSeparator);
                            }
                        }
                        element.name += `_${counter}`;
                        found = this.loadedGeometries.find(item => item.name === element.name);
                    }

                    // Set artificial IDs and indexes for the new geometry
                    element.geometry.features.forEach((feature, index) => {
                        feature.properties.id = index;
                        feature.properties.importedItemIndex = this.loadedGeometries.length;
                    });
                    element.importedItemIndex = this.loadedGeometries.length;

                    this.loadedGeometries.push(element);
                }

                // Set 'selected' attribute to false for all features
                element.geometry.features.forEach(feature => {
                    feature.properties.selected = false;
                });
            });
            this.geometryModified = true
            this.hideImportedObjects()
            this.addGeoJsonMapObjects()
        },
        isFeatureInView(feature, map) {
            const bounds = map.getViewBounds();
            const geometry = feature.geometry;
            const inViewCoords = [];
            if (geometry.type === 'Polygon') {
                const coords = geometry.coordinates[0];
                for (let i = 0; i < coords.length; i++) {
                    const coord = coords[i];
                    if (coord[0] >= bounds.getWest() &&
                        coord[0] <= bounds.getEast() &&
                        coord[1] >= bounds.getSouth() &&
                        coord[1] <= bounds.getNorth()) {
                        inViewCoords.push(coord);
                    }
                }
            } else if (geometry.type === 'LineString') {
                const coords = geometry.coordinates;
                for (let i = 0; i < coords.length; i++) {
                    const coord = coords[i];
                    if (coord[0] >= bounds.getWest() &&
                        coord[0] <= bounds.getEast() &&
                        coord[1] >= bounds.getSouth() &&
                        coord[1] <= bounds.getNorth()) {
                        inViewCoords.push(coord);
                    }
                }
            } else {
                return false;
            }
            if (inViewCoords.length > 0) {
                const middleIndex = Math.floor(inViewCoords.length * 0.15);
                return inViewCoords[middleIndex];
            } else {
                return false;
            }
        },
        showLabel(layer, labelType, coordinates) {
            if(layer && layer.importedItemIndex !== null) {
                const objIndex = this.loadedGeometries.findIndex((item) => item.importedItemIndex === layer.importedItemIndex)
                let geometry = this.loadedGeometries[objIndex].geometry
                if(geometry) {
                    let roadName = null;
                    geometry.features.forEach(feature => {
                        if (feature && feature.properties.id === layer.id) {
                            Object.keys(feature.properties).some(property => {
                                const lowerCaseRoadNames = this.roadNames.map(road => road.toLowerCase());

                                if (lowerCaseRoadNames.includes(property.toLowerCase())) {
                                    roadName = feature.properties[property];
                                    return true;
                                }
                            });
                        }
                    });
                    if(roadName) {
                        this.map.addGeoLabel(coordinates.lat, coordinates.lng, roadName, labelType)
                    }
                }
            }
        },
        updateLabel(index, visibility = true) {
            if (this.loadedGeometries) {
                // Draw labels on the map
                let labelKey = this.loadedGeometries[index].label ? this.loadedGeometries[index].label : null
                let collectionKey = this.loadedGeometries[index].name + '_' + index
                // Remove previous ones
                this.map.removeGeoLabels(collectionKey)
                if (labelKey && visibility) {
                    // Add labels to map
                    let geomObject = this.loadedGeometries[index].geometry
                    geomObject.features.forEach(feature => {
                        let lat, lon
                        let label = feature.properties[labelKey] ? feature.properties[labelKey].toString() : null
                        if (label && label.length > 0) {
                            let featureInView = this.isFeatureInView(feature, this.map)
                            let defaultPos = {}
                            if (Array.isArray(feature.geometry.coordinates[0])) {
                                if(Array.isArray(feature.geometry.coordinates[0][0])) {
                                    defaultPos.lat = feature.geometry.coordinates[0][0][1]
                                    defaultPos.lon = feature.geometry.coordinates[0][0][0]
                                } else {
                                    defaultPos.lat = feature.geometry.coordinates[0][1]
                                    defaultPos.lon = feature.geometry.coordinates[0][0]
                                }
                                lat = featureInView ? featureInView[1] : defaultPos.lat
                                lon = featureInView ? featureInView[0] : defaultPos.lon
                            } else {
                                lat = feature.geometry.coordinates[1]
                                lon = feature.geometry.coordinates[0]
                            }
                            this.map.addGeoLabel(lat, lon, label, collectionKey)
                        }
                    })
                }
            }
        },
        cancel() {
            this.importFromFile = false
        },
        hasEmptyLayers() {
            let hasEmptyLayers = false;
            this.loadedGeometries.forEach(item => {
                if (!item.geometry || !item.geometry.features || item.geometry.features.length < 1) {
                    hasEmptyLayers = true
                }
            })
            return hasEmptyLayers
        },

        async showObservationsToRoadGeometry() {
            if (!this.$refs.observations) return;

            const isInSelection = observation => {
                if (!observation.geometry) return false;
                return this.isGeometryInPolygon(observation.geometry, this.getPolygon())
            }
            let observations = this.$refs.observations
                .getVisibleObservations()
                .filter(isInSelection)
            if (observations.length === 0) return;

            let newGeometry = await this.$refs.observationToRoadGeometryModal.createRoadGeometryFrom(observations, this.getNextItemIndex())
            if (newGeometry == null) return;
            this.clearPolygon()
            this.loadedGeometries.push(newGeometry)
            this.geometryModified = true
            this.map.addGeoJsonLayer(
                JSON.stringify(newGeometry.geometry), false, newGeometry.color, newGeometry.addPolylineEnds,
                1, newGeometry.lineWidth ? newGeometry.lineWidth : 2, newGeometry.visibility
            )
            this.resetDrawing()
        },

        showStreetView(coord) {
            if (coord) {
                this.streetViewDialogCoordinates = {lat: coord.lat, lon: coord.lng}
                this.$nextTick(() => this.$refs.streetView.showPrompt())
            }
        },

    }
}
</script>
<style scoped>

.custom-btn {
    padding: 0.1rem 0.2rem;
    font-size: 0.6rem;
    line-height: 1;
    border-radius: 0.2rem;
}

.editable-input {
    border: none;
    border-bottom: 1px solid #ccc;
    padding: 2px 5px;
    outline: none;
    width: 100%;
}

.editable-input:focus {
    border-bottom: 1px solid #007BFF;
}

.editable-input::placeholder {
    color: #999;
}

.menu-btn {
    border-radius: 0;
    border: 1px solid #8f918a;
}

/** Toolbar **/
.toolbar {
    display: flex;
    flex-wrap: nowrap;
    min-width: max-content;
    align-items: center;
    position: sticky;      /* Make it stay at the top */
    top: 0;               /* Stick to top */
    left: 0;              /* Align to the left */
    z-index: 1000;        /* keep it above elements */
}

.toolbar-item {
    flex: 1;
    position: relative;
}

.toolbar-btn {
    width: 100%;
    padding: 0.4rem 0.8rem;
    background: linear-gradient(to bottom, #505050, #000000);
    color: white;
    border: 1px solid #404041;
    border-bottom: #7FC000 1px solid;
    border-left: none;
    cursor: pointer;
    font-size: 0.9rem;
    border-radius: 0;
    text-transform: uppercase;
    white-space: nowrap;
}

.toolbar-btn:hover:enabled {
    background: #000000;
    color: #BEFF41;
}

.toolbar-btn:disabled {
    color: #bebebe;
    cursor: not-allowed;
}

.dropdown {
    position: relative;
    display: inline-block;
}

.dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    background: white;
    border: 1px solid #ccc;
    min-width: 150px;
    padding: 0.25rem 0;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    display: block; /* ensure it renders */
}

.dropdown-item {
    width: 100%;
    padding: 0.5rem;
    background: white;
    border: none;
    text-align: left;
    cursor: pointer;
}

.dropdown-item:disabled {
    cursor: not-allowed;
    color: #d5d4d4 !important;
}

.dropdown-item:hover:enabled {
    background-color: #f0f0f0;
}

.dropdown-menu-left-top {
    position: fixed !important;
    min-width: 12rem;
    background-color: #fff;
    border: 1px solid #7FC000;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
    z-index: 9999;
}

.table th {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 100px;
}

.table-row-highlighted {
    background-color: #e3f2fd !important;
    border-left: 4px solid #2196f3 !important;
}

.table-row-highlighted:hover {
    background-color: #bbdefb !important;
}

tbody tr {
    transition: background-color 0.2s ease;
}

tbody tr:hover {
    background-color: #f5f5f5;
}
</style>
